import { CircularProgress } from '@mui/material'
import { Button, pantryColors } from '@pantry/ui'
import React from 'react'
import type { CampaignHistoryEventType } from '../../../../types/loyaltyMarketing'
import { useLoyaltyCampaignHistoryQuery } from '../../hooks/useLoyaltyMarketing'
import { describeCampaignHistoryEvent } from '../campaignDisplay'
import { EMERALD } from './CampaignTypeIcon'

const p = pantryColors

// "MMM D at h:mm A" — the mock's Activity-timeline timestamp format
// (e.g. "Jan 15 at 10:02 AM").
const formatWhen = (datetime?: string): string => {
  if (!datetime) return '—'
  const parsed = new Date(datetime)
  if (Number.isNaN(parsed.getTime())) return datetime
  const day = parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
  const time = parsed.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${day} at ${time}`
}

// Dot color per the mock's Activity tab: created / content edits = gray,
// status_changed / send_started / send_resumed = blue, send_completed =
// green, send_on_hold = amber, send_failed = red. The event + detail lines come from
// describeCampaignHistoryEvent (campaignDisplay) — `detail` is an object on
// the wire and must never be rendered as a React child.
const dotColor = (event: CampaignHistoryEventType): string => {
  switch (event) {
    case 'status_changed':
    case 'send_started':
    case 'send_resumed':
      return p.brand.primary
    case 'send_completed':
      return EMERALD
    case 'send_on_hold':
      return p.amber[700]
    case 'send_failed':
      return p.red[700]
    default:
      return p.gray[400]
  }
}

// Mockup Activity tab rows (`.activity-item`): dot + event line + time. Used by
// the detail drawer; paginates with the campaign history cursor feed.
export const CampaignActivityList: React.FC<{
  storeId: string
  campaignId: string
  enabled?: boolean
}> = ({ storeId, campaignId, enabled = true }) => {
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLoyaltyCampaignHistoryQuery(storeId, campaignId, { enabled })
  const events = (data?.pages ?? []).flatMap((page) => page.events)

  if (isLoading)
    return <ActivityMessage loading>Loading history…</ActivityMessage>
  if (isError)
    return (
      <ActivityMessage>
        Couldn't load campaign history. Please try again.
      </ActivityMessage>
    )
  if (events.length === 0)
    return <ActivityMessage>No history yet.</ActivityMessage>

  return (
    <div>
      {events.map((event) => {
        const { text, sub } = describeCampaignHistoryEvent(event)
        return (
          <ActivityRow
            key={event.id}
            color={dotColor(event.event)}
            text={text}
            sub={sub}
            when={formatWhen(event.created_at)}
          />
        )
      })}
      {hasNextPage && (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
        >
          <Button
            variant="ghost"
            size="sm"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}

// Shared row + empty/loading message, exported for FlowActivityList.
export const ActivityRow: React.FC<{
  color: string
  text: string
  sub?: string
  when: string
}> = ({ color, text, sub, when }) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      padding: '11px 0',
      borderBottom: `1px solid ${p.gray[100]}`,
    }}
  >
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        marginTop: 5,
        background: color,
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, color: p.gray[700] }}>{text}</div>
      {sub && (
        <div style={{ fontSize: 12, color: p.gray[500], marginTop: 2 }}>
          {sub}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: p.gray[400], marginTop: 2 }}>
        {when}
      </div>
    </div>
  </div>
)

export const ActivityMessage: React.FC<{
  loading?: boolean
  children: React.ReactNode
}> = ({ loading, children }) => (
  <div
    style={{
      padding: '32px 0',
      textAlign: 'center',
      fontSize: 13,
      color: p.gray[500],
    }}
  >
    {loading && (
      <div style={{ marginBottom: 12 }}>
        <CircularProgress
          size={20}
          thickness={5}
          sx={{ color: p.brand.primary }}
        />
      </div>
    )}
    {children}
  </div>
)
